import React, { type Ref, useState } from 'react';

import { AnimatePresence } from 'framer-motion';

import { IconEyeHide, IconEyeShow } from '@pkg/assets/icons';

import { FieldText, type FieldTextProps } from '../FieldText';
import { tokens as fieldTextTokens } from '../FieldText/FieldText.tokens';

import { EyeIconWrapper, ShowHideAction, ShowHideWrapper } from './FieldPassword.style';
import { tokens } from './FieldPassword.tokens';

type FieldTextSubset = Pick<
    FieldTextProps,
    | 'inputRef'
    | 'id'
    | 'name'
    | 'value'
    | 'label'
    | 'placeholder'
    | 'prefix'
    | 'prefixOffset'
    | 'prefixPassthrough'
    | 'size'
    | 'theme'
    | 'autoFocus'
    | 'backdropBlur'
    | 'disabled'
    | 'error'
    | 'required'
    | 'onBlur'
    | 'onFocus'
    | 'onChange'
>;

export interface FieldPasswordProps extends FieldTextSubset {
    wrapperRef?: Ref<HTMLDivElement>;
}

function getSuffixOffset(size: FieldTextProps['size'] = 'medium') {
    return tokens.suffixAction[size] - fieldTextTokens.gap[size];
}

export function FieldPassword({ wrapperRef, value = '', onChange, ...rest }: FieldPasswordProps) {
    const noInteraction = Boolean(rest.disabled || !onChange);

    const [show, setShow] = useState(false);

    function handleShowToggle() {
        setShow((current) => !current);
    }

    const suffixActionMarkup = noInteraction ? null : (
        <ShowHideWrapper $show={show} $size={rest.size} $theme={rest.theme}>
            <ShowHideAction preventFocus onAction={handleShowToggle}>
                <EyeIconWrapper>{show ? <IconEyeHide /> : <IconEyeShow />}</EyeIconWrapper>
            </ShowHideAction>
        </ShowHideWrapper>
    );

    // TODO: We may want to move `AnimatePresence` into the
    // `FieldText > Suffix` container.
    const suffixMarkup = <AnimatePresence>{suffixActionMarkup}</AnimatePresence>;
    const suffixOffset = suffixActionMarkup ? getSuffixOffset(rest.size) : undefined;

    return (
        <FieldText
            ref={wrapperRef}
            type={show ? 'text' : 'password'}
            value={value}
            suffix={suffixMarkup}
            suffixOffset={suffixOffset}
            onChange={onChange}
            {...rest}
        />
    );
}
